import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  CameraControls,
  Environment,
  Grid,
  Html,
  useGLTF,
  GizmoHelper,
  GizmoViewport,
  Bvh,
} from '@react-three/drei';
import * as THREE from 'three';
import { R1AS_ROOM_LABELS } from '../../data/r1asRoomLabels';
import {
  localizeFromBox3,
  layerKeyFromName,
} from '../../services/modelLocalize';
import {
  classifyMeshes,
  DEFAULT_FEATURE_VISIBILITY,
} from '../../services/modelFeatureMap';
import LabWalkControls, {
  buildWalkSpawn,
  WALK_EYE_FT,
} from './LabWalkControls';
import { FaucetPointerBridge, FaucetWaterFx } from './LabFaucetWater';
import {
  LabAnnotateBridge,
  LabAnnotationHitProxies,
  LabAnnotationMarkers,
} from './LabAnnotate';
import {
  R1AS_FAUCETS,
  R1AS_FAUCET_BY_MESH,
} from '../../data/r1asFaucets';
import {
  spoutFromAnnotations,
  spoutPinFromAnnotations,
} from '../../services/labAnnotations';
import LabStoveClock from './LabStoveClock';
import { R1AS_STOVE_DISPLAY } from '../../data/r1asStoveDisplay';
import LabTvScreen from './LabTvScreen';
import LabExterior from './LabExterior';
import { ambientSetIndoor } from '../../services/labAmbientAudio';

/** SoftPlan inches → meters (same as site planner / R3F viewer) */
const INCH_TO_M = 1 / 39.37;

const DOOR_HIGHLIGHT = new THREE.Color('#ff6a33');

/** Prefer annotated stove-display pin; else authored pose. */
function stoveDisplayPose(annotations) {
  const pins = (annotations || []).filter(
    (a) => a.kind === 'stove-display' && Array.isArray(a.position)
  );
  if (!pins.length) return R1AS_STOVE_DISPLAY;
  const last = pins[pins.length - 1];
  return {
    ...R1AS_STOVE_DISPLAY,
    position: last.position,
  };
}

/**
 * Screen-space guide label — shrinks as the camera approaches
 * (drei Html distanceFactor does the opposite and blows up up close).
 */
function LabGuideLabel({
  position,
  className,
  children,
  zIndexRange = [30, 0],
  /** World distance (m) where scale ≈ 1 */
  refDistance = 10,
  minScale = 0.22,
  maxScale = 1,
  /** Hide when camera closer than this (m, world) */
  hideBelow = 1.35,
}) {
  const { camera } = useThree();
  const wrapRef = useRef(null);
  const anchorRef = useRef(null);
  const world = useRef(new THREE.Vector3());

  useFrame(() => {
    const el = wrapRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;
    anchor.getWorldPosition(world.current);
    const dist = camera.position.distanceTo(world.current);
    if (dist < hideBelow) {
      el.style.opacity = '0';
      el.style.transform = 'scale(0.01)';
      return;
    }
    const scale = Math.min(
      maxScale,
      Math.max(minScale, dist / refDistance)
    );
    const fade = Math.min(1, (dist - hideBelow) / 1.2);
    el.style.opacity = String(fade);
    el.style.transform = `scale(${scale})`;
  });

  return (
    <group ref={anchorRef} position={position}>
      <Html
        center
        style={{ pointerEvents: 'none' }}
        zIndexRange={zIndexRange}
      >
        <div
          ref={wrapRef}
          className="lab-guide-label-scale"
          style={{ transformOrigin: 'center center' }}
        >
          <div className={className}>{children}</div>
        </div>
      </Html>
    </group>
  );
}

/**
 * Bare R3F lab canvas — docs/SIMULATION_LAB.md · docs/MODEL_FEATURE_MAP.md
 * Horizontal section cut — docs/SIM_LAB_SECTION_CUT.md
 * Exterior envs — docs/SIM_LAB_EXTERIOR_ENV.md
 */
export default function LabViewer({
  modelUrl,
  slug = 'r1as',
  layers = { roof: true, walls: true, interior: true },
  showLabels = false,
  /** Footprint ring + entry marker (docs/SIMULATION_LAB.md floor/entry contract). */
  showLocalizeGuides = true,
  featureVisibility = DEFAULT_FEATURE_VISIBILITY,
  highlightDoors = false,
  /**
   * Horizontal cross-section — clip geometry above cutY.
   * `{ enabled, t }` with t ∈ [0,1] from floor → roof.
   */
  sectionCut = { enabled: false, t: 0.85 },
  /** Exterior place id (`none` | `beach` | …) — docs/SIM_LAB_EXTERIOR_ENV.md */
  exteriorId = 'none',
  /** Optional sky variant asset id for the active exterior. */
  exteriorSkyId = null,
  exteriorLayers = { sky: true, ground: true },
  /** First-person 6 ft walk — docs/SIM_LAB_WALK_CAMERA.md */
  walkMode = false,
  onWalkExitRequest,
  /** Walk annotate — docs/SIM_LAB_ANNOTATE.md */
  annotateMode = false,
  annotations = [],
  annotateKind = 'faucet-hit',
  annotateStationId = 'sink-vanity',
  annotateSelectedId = null,
  onAnnotationAdd,
  onAnnotateSelect,
  padding = 1.25,
  onStats,
  onFeatureMap,
  onBindApi,
  className = '',
}) {
  const controlsRef = useRef(null);
  const apiRef = useRef({});
  const walkLookApiRef = useRef(null);
  const featureMapRef = useRef(null);
  const [worldBox, setWorldBox] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [doorHints, setDoorHints] = useState([]);

  const localize = useMemo(() => {
    if (!worldBox) return null;
    return localizeFromBox3(worldBox, {
      padding,
      doorHints: doorHints.length ? doorHints : undefined,
    });
  }, [worldBox, padding, doorHints]);

  const walkSpawn = useMemo(
    () => (walkMode && localize ? buildWalkSpawn(localize) : null),
    [walkMode, localize]
  );

  /** World-Y plane: keep below cut, clip above. Docs: SIM_LAB_SECTION_CUT.md */
  const sectionPlane = useMemo(() => {
    if (!sectionCut?.enabled || !localize) return null;
    const t = Math.min(1, Math.max(0, Number(sectionCut.t) || 0));
    const height = Math.max(0.05, Number(localize.size?.[1]) || 0.05);
    const floorY = Number.isFinite(localize.floorY) ? localize.floorY : 0;
    const cutY = floorY + t * height;
    return new THREE.Plane(new THREE.Vector3(0, -1, 0), cutY);
  }, [sectionCut?.enabled, sectionCut?.t, localize]);

  const setLookAt = useCallback((ex, ey, ez, lx, ly, lz, animate = true) => {
    const controls = controlsRef.current;
    if (!controls) return;
    controls.setLookAt(ex, ey, ez, lx, ly, lz, animate);
  }, []);

  const goToPreset = useCallback(
    (key, animate = true) => {
      if (!localize?.presets?.[key]) return;
      const { eye, lookAt } = localize.presets[key];
      setLookAt(
        eye[0],
        eye[1],
        eye[2],
        lookAt[0],
        lookAt[1],
        lookAt[2],
        animate
      );
    },
    [localize, setLookAt]
  );

  // Frame once per front-axis resolve (v0 default, then v1 when doors arrive).
  const framedFrontRef = useRef(null);
  useEffect(() => {
    if (!localize || walkMode) return;
    const key = `${localize.frontAxis}:${localize.frontSource || 'default'}`;
    if (framedFrontRef.current === key) return;
    framedFrontRef.current = key;
    goToPreset('iso', true);
  }, [localize, goToPreset, walkMode]);

  useEffect(() => {
    framedFrontRef.current = null;
    setDoorHints([]);
  }, [modelUrl]);

  useEffect(() => {
    if (!localize) return;
    onStats?.({
      slug,
      modelUrl,
      size: localize.size,
      center: localize.center,
      span: localize.span,
      floorY: localize.floorY,
      frontAxis: localize.frontAxis,
      frontSource: localize.frontSource,
      entry: localize.entry,
      version: localize.version,
      confidence: localize.confidence,
      walkEyeFt: walkMode ? WALK_EYE_FT : null,
    });
  }, [localize, slug, modelUrl, onStats, walkMode]);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    controls.enabled = !walkMode;
  }, [walkMode]);

  const handleFeatureMap = useCallback(
    (map) => {
      featureMapRef.current = map;
      const doors = (map?.features || []).filter(
        (f) => f.kind === 'door' && !f.parentId && Array.isArray(f.center)
      );
      setDoorHints(doors);
      onFeatureMap?.(map);
    },
    [onFeatureMap]
  );

  useEffect(() => {
    apiRef.current.goToPreset = goToPreset;
    apiRef.current.setLookAt = setLookAt;
    apiRef.current.getLocalize = () => localize;
    apiRef.current.getFeatureMap = () => featureMapRef.current;
    apiRef.current.getSectionCut = () => sectionCut;
    apiRef.current.getExteriorId = () => exteriorId;
    onBindApi?.(apiRef.current);
  }, [goToPreset, setLookAt, localize, onBindApi, sectionCut, exteriorId]);

  const exteriorActive = exteriorId && exteriorId !== 'none';

  return (
    <div
      className={`lab-viewer ${className}`.trim()}
      data-walk={walkMode ? 'true' : 'false'}
      data-exterior={exteriorActive ? exteriorId : 'none'}
    >
      {loadError && (
        <div className="lab-viewer-error" role="alert">
          {loadError}
        </div>
      )}
      {walkMode && !annotateMode ? (
        <div className="lab-walk-hud" aria-live="polite">
          Walk · 6 ft · WASD · Q/E · drag look · N annotate · Esc exit
        </div>
      ) : null}
      <Canvas
        shadows
        dpr={[1, 1.75]}
        camera={{ position: [12, 7, 14], fov: walkMode ? 60 : 40, near: 0.05, far: 2000 }}
        gl={{
          antialias: true,
          preserveDrawingBuffer: true,
          localClippingEnabled: true,
          toneMapping: THREE.ACESFilmicToneMapping,
        }}
      >
        <ClippingSetup enabled={Boolean(sectionPlane)} />
        {!exteriorActive ? (
          <color attach="background" args={['#d9d4cc']} />
        ) : null}
        <ambientLight intensity={exteriorActive ? 0.25 : 0.45} />
        <directionalLight
          intensity={exteriorActive ? 0.25 : 0.4}
          position={[-8, 14, -6]}
        />
        <directionalLight
          intensity={exteriorActive ? 0.45 : 0.55}
          position={[12, 18, 10]}
          castShadow
        />

        <Suspense fallback={<LoaderNote label={slug} />}>
          <Bvh firstHitOnly>
            <LabModel
              key={modelUrl}
              url={modelUrl}
              slug={slug}
              layers={layers}
              showLabels={showLabels}
              featureVisibility={featureVisibility}
              highlightDoors={highlightDoors}
              sectionPlane={sectionPlane}
              annotateMode={annotateMode}
              annotations={annotations}
              annotateKind={annotateKind}
              annotateStationId={annotateStationId}
              annotateSelectedId={annotateSelectedId}
              onAnnotationAdd={onAnnotationAdd}
              onAnnotateSelect={onAnnotateSelect}
              onWorldBox={setWorldBox}
              onFeatureMap={handleFeatureMap}
              onError={setLoadError}
            />
          </Bvh>
          {!exteriorActive ? <Environment preset="warehouse" /> : null}
          {exteriorActive ? (
            <LabExterior
              exteriorId={exteriorId}
              skyAssetId={exteriorSkyId}
              layers={exteriorLayers}
              localize={localize}
              walkMode={walkMode}
            />
          ) : null}
        </Suspense>

        {!exteriorActive ? (
          <Grid
            position={[0, -0.001, 0]}
            args={[80, 80]}
            cellSize={0.5}
            cellThickness={0.55}
            cellColor="#b5aea3"
            sectionSize={2.5}
            sectionThickness={1.05}
            sectionColor="#8a8278"
            fadeDistance={56}
            fadeStrength={1.15}
            infiniteGrid
          />
        ) : null}
        {/* World floor at Y≈0 — models are recentered so bbox.min.y ≈ 0.
            labWalkIgnore: walk mode prefers model floors/stairs over this pad.
            Hidden when an exterior is active — the sand apron takes over. */}
        {!exteriorActive ? (
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, -0.012, 0]}
            receiveShadow
            userData={{ labWalkIgnore: true }}
          >
            <planeGeometry args={[160, 160]} />
            <meshStandardMaterial
              color="#cdc6bc"
              roughness={0.95}
              metalness={0}
            />
          </mesh>
        ) : null}

        {showLocalizeGuides && localize ? (
          <LocalizeGuides localize={localize} walkMode={walkMode} />
        ) : null}

        <CameraControls
          ref={controlsRef}
          makeDefault
          enabled={!walkMode}
          minDistance={1}
          maxDistance={400}
          minPolarAngle={0.08}
          maxPolarAngle={Math.PI * 0.49}
          dollySpeed={0.85}
          truckSpeed={1.2}
          smoothTime={0.35}
        />

        <LabWalkControls
          enabled={walkMode}
          floorY={localize?.floorY ?? 0}
          spawn={walkSpawn}
          onExitRequest={onWalkExitRequest}
          lookApiRef={walkLookApiRef}
        />

        <AmbientMuffleBridge
          walkMode={walkMode}
          footprint={localize?.footprint}
        />

        <CaptureBridge apiRef={apiRef} />

        <GizmoHelper alignment="bottom-right" margin={[64, 64]}>
          <GizmoViewport
            axisColors={['#c45c3e', '#5a8f5a', '#4a6fa5']}
            labelColor="white"
            onClick={(e) => {
              if (!walkMode) return;
              const p = e.object?.position;
              if (!p) return;
              walkLookApiRef.current?.faceAxis?.(p.x, p.y, p.z);
              e.stopPropagation();
            }}
          />
        </GizmoHelper>
      </Canvas>
    </div>
  );
}

/** Floor footprint ring + entry guides — docs/SIMULATION_LAB.md */
function LocalizeGuides({ localize, walkMode = false }) {
  const { floorY = 0, footprint, entry } = localize;
  const r = Math.max(footprint?.radius || 2, 1.2);
  const cx = footprint?.cx ?? 0;
  const cz = footprint?.cz ?? 0;
  const ep = entry?.point || [cx, floorY + 1.5, cz];
  const out = entry?.outward || [0, 0, 1];
  const stub = [
    ep[0] + out[0] * 1.1,
    floorY + 0.05,
    ep[2] + out[2] * 1.1,
  ];

  // Walk POV: no solid dots (they fill the frame up close) — just "enter" copy.
  if (walkMode) {
    return (
      <group name="localize-guides-walk">
        <mesh
          position={[cx, floorY + 0.02, cz]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <ringGeometry args={[r * 0.96, r * 1.005, 96]} />
          <meshBasicMaterial color="#1c1c1a" transparent opacity={0.12} />
        </mesh>
        <LabGuideLabel
          position={[ep[0], ep[1], ep[2]]}
          className="lab-enter-walk"
          zIndexRange={[30, 0]}
          refDistance={6}
          minScale={0.35}
          maxScale={0.55}
          hideBelow={0.55}
        >
          enter
        </LabGuideLabel>
      </group>
    );
  }

  return (
    <group name="localize-guides">
      <mesh
        position={[cx, floorY + 0.02, cz]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <ringGeometry args={[r * 0.96, r * 1.005, 96]} />
        <meshBasicMaterial color="#1c1c1a" transparent opacity={0.22} />
      </mesh>
      <mesh position={[cx, floorY + 0.012, cz]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.08, 20]} />
        <meshBasicMaterial color="#1c1c1a" transparent opacity={0.28} />
      </mesh>
      <mesh position={ep}>
        <sphereGeometry args={[0.07, 16, 12]} />
        <meshBasicMaterial color="#1c1c1a" transparent opacity={0.55} />
      </mesh>
      <mesh position={stub}>
        <sphereGeometry args={[0.04, 12, 10]} />
        <meshBasicMaterial color="#1c1c1a" transparent opacity={0.35} />
      </mesh>
      <LabGuideLabel
        position={[ep[0], ep[1] + 0.28, ep[2]]}
        className="lab-entry-label"
        zIndexRange={[30, 0]}
        refDistance={12}
      >
        entry
      </LabGuideLabel>
      <LabGuideLabel
        position={[cx, floorY + 0.22, cz]}
        className="lab-floor-label"
        zIndexRange={[28, 0]}
        refDistance={14}
      >
        floor
      </LabGuideLabel>
    </group>
  );
}

function CaptureBridge({ apiRef }) {
  const { gl, scene, camera } = useThree();

  useEffect(() => {
    apiRef.current.waitSettled = () =>
      new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

    apiRef.current.capturePng = async () => {
      gl.render(scene, camera);
      const blob = await new Promise((resolve, reject) => {
        gl.domElement.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
          'image/png'
        );
      });
      return blob;
    };
  }, [gl, scene, camera, apiRef]);

  return null;
}

function ClippingSetup({ enabled }) {
  const { gl } = useThree();
  useEffect(() => {
    gl.localClippingEnabled = Boolean(enabled);
  }, [gl, enabled]);
  return null;
}

/**
 * Feed the ambient engine an indoor/outdoor signal — walk mode inside the
 * structure footprint muffles the soundscape. Docs: SIM_LAB_AMBIENT_AUDIO.md
 */
function AmbientMuffleBridge({ walkMode, footprint }) {
  const { camera } = useThree();
  const frameRef = useRef(0);

  useEffect(() => () => ambientSetIndoor(false), []);

  useFrame(() => {
    frameRef.current += 1;
    if (frameRef.current % 8 !== 0) return;
    if (!walkMode || !footprint) {
      ambientSetIndoor(false);
      return;
    }
    const mx = (Number(footprint.width) || 0) / 2 + 0.4;
    const mz = (Number(footprint.depth) || 0) / 2 + 0.4;
    const inside =
      Math.abs(camera.position.x - (Number(footprint.cx) || 0)) <= mx &&
      Math.abs(camera.position.z - (Number(footprint.cz) || 0)) <= mz;
    ambientSetIndoor(inside);
  });

  return null;
}

function applyClippingPlanes(material, planes) {
  const mats = Array.isArray(material) ? material : [material];
  mats.forEach((mat) => {
    if (!mat) return;
    mat.clippingPlanes = planes;
    mat.clipShadows = planes.length > 0;
    mat.needsUpdate = true;
  });
}

function LoaderNote({ label }) {
  return (
    <Html center>
      <p className="lab-loader">Loading {label || 'model'}…</p>
    </Html>
  );
}

function LabModel({
  url,
  slug,
  layers,
  showLabels,
  featureVisibility,
  highlightDoors,
  sectionPlane = null,
  annotateMode = false,
  annotations = [],
  annotateKind = 'faucet-hit',
  annotateStationId = 'sink-vanity',
  annotateSelectedId = null,
  onAnnotationAdd,
  onAnnotateSelect,
  onWorldBox,
  onFeatureMap,
  onError,
}) {
  const { scene: gltfScene } = useGLTF(url);
  const [faucetOn, setFaucetOn] = useState(() => new Set());
  const faucetTargetsRef = useRef([]);
  const faucetObjByUuid = useRef(new Map());

  const meshes = useMemo(() => prepareMeshes(gltfScene), [gltfScene]);
  const labels = slug === 'r1as' ? R1AS_ROOM_LABELS : [];
  const faucetEnabled = slug === 'r1as';

  const meshByName = useMemo(() => {
    const map = new Map();
    meshes.forEach((m) => {
      if (m.name) map.set(m.name, m);
    });
    return map;
  }, [meshes]);

  const toggleFaucet = useCallback((stationId) => {
    setFaucetOn((prev) => {
      const next = new Set(prev);
      if (next.has(stationId)) next.delete(stationId);
      else next.add(stationId);
      return next;
    });
  }, []);

  const bindFaucetTarget = useCallback((uuid, stationId, obj) => {
    if (obj && stationId) {
      obj.userData.faucetStationId = stationId;
      faucetObjByUuid.current.set(uuid, obj);
    } else {
      faucetObjByUuid.current.delete(uuid);
    }
    faucetTargetsRef.current = Array.from(faucetObjByUuid.current.values());
  }, []);

  const bindAnnHit = useCallback((annId, stationId, obj) => {
    const key = `ann:${annId}`;
    if (obj && stationId) {
      obj.userData.faucetStationId = stationId;
      faucetObjByUuid.current.set(key, obj);
    } else {
      faucetObjByUuid.current.delete(key);
    }
    faucetTargetsRef.current = Array.from(faucetObjByUuid.current.values());
  }, []);

  const featureMap = useMemo(() => {
    if (!meshes.length) return null;
    return classifyMeshes(
      meshes.map((m) => ({
        id: m.uuid,
        uuid: m.uuid,
        name: m.name,
        size: m.sizeM,
        center: m.centerM,
        min: m.minM,
        max: m.maxM,
        color: m.color,
      }))
    );
  }, [meshes]);

  const featureById = useMemo(() => {
    const map = new Map();
    featureMap?.features?.forEach((f) => map.set(f.id, f));
    return map;
  }, [featureMap]);

  const doorHighlightMats = useMemo(() => {
    if (!highlightDoors) return null;
    const map = new Map();
    meshes.forEach((m) => {
      const kind = featureById.get(m.uuid)?.kind;
      if (kind === 'door') map.set(m.uuid, cloneHighlightMaterial(m.material));
    });
    return map;
  }, [highlightDoors, meshes, featureById]);

  useEffect(() => {
    onError?.(null);
  }, [url, onError]);

  useEffect(() => {
    onFeatureMap?.(featureMap);
  }, [featureMap, onFeatureMap]);

  useEffect(() => {
    if (!meshes.length) {
      onWorldBox?.(null);
      return;
    }
    const group = new THREE.Group();
    group.scale.setScalar(INCH_TO_M);
    meshes.forEach((m) => {
      const mesh = new THREE.Mesh(m.geometry);
      mesh.position.fromArray(m.position);
      mesh.quaternion.fromArray(m.quaternion);
      mesh.scale.fromArray(m.scale);
      group.add(mesh);
    });
    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    onWorldBox?.(box);
  }, [meshes, onWorldBox]);

  useEffect(() => {
    setFaucetOn(new Set());
    faucetTargetsRef.current = [];
    faucetObjByUuid.current.clear();
  }, [url]);

  const clipPlanes = useMemo(
    () => (sectionPlane ? [sectionPlane] : []),
    [sectionPlane]
  );

  // Keep material clipping in sync for base + door-highlight clones.
  useEffect(() => {
    meshes.forEach((m) => applyClippingPlanes(m.material, clipPlanes));
    doorHighlightMats?.forEach((mat) => applyClippingPlanes(mat, clipPlanes));
    return () => {
      meshes.forEach((m) => applyClippingPlanes(m.material, []));
      doorHighlightMats?.forEach((mat) => applyClippingPlanes(mat, []));
    };
  }, [meshes, doorHighlightMats, clipPlanes]);

  return (
    <>
      <group scale={INCH_TO_M}>
        {meshes.map((m) => {
          const feature = featureById.get(m.uuid);
          const kind = feature?.kind || 'structure';
          const layerKey = feature?.layerKey || layerKeyFromName(m.name);
          if (layers && layers[layerKey] === false) return null;
          if (featureVisibility && featureVisibility[kind] === false) {
            return null;
          }

          const material =
            (highlightDoors && doorHighlightMats?.get(m.uuid)) || m.material;
          const station =
            faucetEnabled ? R1AS_FAUCET_BY_MESH.get(m.name) : null;

          return (
            <mesh
              key={m.uuid}
              geometry={m.geometry}
              material={material}
              position={m.position}
              quaternion={m.quaternion}
              scale={m.scale}
              castShadow
              receiveShadow
              userData={{
                featureKind: kind,
                featureId: m.uuid,
                faucetStationId: station?.id || null,
                labWalkSurface: true,
              }}
              onUpdate={(self) => {
                applyClippingPlanes(self.material, clipPlanes);
              }}
              ref={
                station
                  ? (obj) => bindFaucetTarget(m.uuid, station.id, obj)
                  : undefined
              }
            />
          );
        })}
        {showLabels &&
          labels.map((room) => (
            <LabGuideLabel
              key={room.name}
              position={room.position}
              className="lab-room-label"
              zIndexRange={[20, 0]}
              refDistance={11}
            >
              {room.name}
            </LabGuideLabel>
          ))}
      </group>

      <LabAnnotationMarkers
        annotations={annotations}
        selectedId={annotateSelectedId}
        annotateMode={annotateMode}
        onSelect={onAnnotateSelect}
      />

      <LabAnnotationHitProxies
        annotations={annotations}
        onRegister={bindAnnHit}
      />

      <LabAnnotateBridge
        enabled={annotateMode}
        slug={slug}
        kind={annotateKind}
        stationId={annotateStationId}
        onAdd={onAnnotationAdd}
      />

      {slug === 'r1as' ? (
        <LabStoveClock
          enabled
          pose={stoveDisplayPose(annotations)}
        />
      ) : null}

      {slug === 'r2as' ? <LabTvScreen enabled /> : null}

      {faucetEnabled ? (
        <FaucetPointerBridge
          enabled
          suppress={annotateMode}
          targetsRef={faucetTargetsRef}
          onToggle={toggleFaucet}
        />
      ) : null}

      {faucetEnabled
        ? R1AS_FAUCETS.map((station) => {
            const spoutPin = spoutPinFromAnnotations(annotations, station.id);
            const spout = station.spoutMesh
              ? meshByName.get(station.spoutMesh)
              : null;
            // Prefer authored spout pin → mesh bottom-face → hit fallback (annotate-only)
            const spoutOverride =
              spoutPin ||
              (!spout
                ? spoutFromAnnotations(annotations, station.id, {
                    allowHitFallback: true,
                  })
                : null);
            if (!spout && !spoutOverride) return null;
            return (
              <FaucetWaterFx
                key={station.id}
                station={station}
                spoutBounds={
                  spout
                    ? {
                        min: spout.minM,
                        max: spout.maxM,
                        center: spout.centerM,
                      }
                    : null
                }
                spoutGeometry={spout?.geometry || null}
                spoutPose={
                  spout
                    ? {
                        position: spout.position,
                        quaternion: spout.quaternion,
                        scale: spout.scale,
                      }
                    : null
                }
                spoutOverride={spoutOverride}
                parentScale={INCH_TO_M}
                active={faucetOn.has(station.id)}
              />
            );
          })
        : null}

      {/* Larger invisible click proxies at mesh centers — SoftPlan fixtures are tiny */}
      {faucetEnabled
        ? R1AS_FAUCETS.flatMap((station) => {
            const radius = station.hitRadius || (station.type === 'shower' ? 0.16 : 0.1);
            return (station.meshNames || [])
              .map((name) => {
                const m = meshByName.get(name);
                if (!m?.centerM) return null;
                const key = `proxy:${station.id}:${name}`;
                return (
                  <mesh
                    key={key}
                    position={m.centerM}
                    userData={{
                      faucetStationId: station.id,
                      annotateIgnore: true,
                    }}
                    ref={(obj) => bindFaucetTarget(key, station.id, obj)}
                  >
                    <sphereGeometry args={[radius, 12, 10]} />
                    <meshBasicMaterial
                      transparent
                      opacity={0.001}
                      depthWrite={false}
                      colorWrite={false}
                    />
                  </mesh>
                );
              })
              .filter(Boolean);
          })
        : null}
    </>
  );
}

function cloneHighlightMaterial(source) {
  const mats = Array.isArray(source) ? source : [source];
  const out = mats.map((mat) => {
    if (!mat) return mat;
    const next = mat.clone();
    if (next.color) next.color.copy(DOOR_HIGHLIGHT);
    if ('emissive' in next && next.emissive) {
      next.emissive.copy(DOOR_HIGHLIGHT);
      next.emissiveIntensity = 0.35;
    }
    next.needsUpdate = true;
    return next;
  });
  return Array.isArray(source) ? out : out[0];
}

function prepareMeshes(scene) {
  const root = scene.clone(true);
  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  root.position.x -= center.x;
  root.position.y -= center.y;
  root.position.z -= center.z;
  root.position.y += size.y / 2;
  root.updateMatrixWorld(true);

  const list = [];
  const tmpBox = new THREE.Box3();
  const tmpSize = new THREE.Vector3();
  const tmpCenter = new THREE.Vector3();
  root.traverse((obj) => {
    if (!obj.isMesh) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((mat) => {
      if (!mat) return;
      mat.side = THREE.DoubleSide;
      mat.needsUpdate = true;
    });
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    obj.matrixWorld.decompose(pos, quat, scl);

    tmpBox.setFromObject(obj);
    tmpBox.getSize(tmpSize);
    tmpBox.getCenter(tmpCenter);
    const sizeM = [
      tmpSize.x * INCH_TO_M,
      tmpSize.y * INCH_TO_M,
      tmpSize.z * INCH_TO_M,
    ];
    const centerM = [
      tmpCenter.x * INCH_TO_M,
      tmpCenter.y * INCH_TO_M,
      tmpCenter.z * INCH_TO_M,
    ];
    const minM = [
      tmpBox.min.x * INCH_TO_M,
      tmpBox.min.y * INCH_TO_M,
      tmpBox.min.z * INCH_TO_M,
    ];
    const maxM = [
      tmpBox.max.x * INCH_TO_M,
      tmpBox.max.y * INCH_TO_M,
      tmpBox.max.z * INCH_TO_M,
    ];
    const mat0 = mats[0];
    const color = mat0?.color ? `#${mat0.color.getHexString()}` : null;

    list.push({
      uuid: obj.uuid,
      name: obj.name || obj.parent?.name || '',
      geometry: obj.geometry,
      material: obj.material,
      position: pos.toArray(),
      quaternion: [quat.x, quat.y, quat.z, quat.w],
      scale: scl.toArray(),
      sizeM,
      centerM,
      minM,
      maxM,
      color,
    });
  });
  return list;
}
